/**
 * UserFilters Component
 *
 * Filtering controls for the user list.
 */

import React from 'react';
import { UsersFilter } from '../hooks';

interface UserFiltersProps {
  filter: UsersFilter;
  onChange: (filter: UsersFilter) => void;
  totalCount: number;
}

export const UserFilters: React.FC<UserFiltersProps> = ({
  filter,
  onChange,
  totalCount,
}) => {
  return (
    <div className="bg-white rounded-xl shadow-sm p-4">
      <div className="flex flex-col sm:flex-row gap-4">
        {/* Search */}
        <div className="flex-1">
          <div className="relative">
            <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search by name or email..."
              value={filter.search || ''}
              onChange={(e) => onChange({ ...filter, search: e.target.value })}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>
        </div>

        {/* Role Filter */}
        <select
          value={filter.role || ''}
          onChange={(e) =>
            onChange({
              ...filter,
              role: e.target.value as UsersFilter['role'],
            })
          }
          className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white"
        >
          <option value="">All Roles</option>
          <option value="guest">Guest</option>
          <option value="host">Host</option>
          <option value="admin">Admin</option>
        </select>

        {/* Status Filter */}
        <select
          value={filter.status || ''}
          onChange={(e) =>
            onChange({
              ...filter,
              status: e.target.value as UsersFilter['status'],
            })
          }
          className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white"
        >
          <option value="">All Status</option>
          <option value="active">Active</option>
          <option value="suspended">Suspended</option>
        </select>

        {/* Legacy migration filter */}
        <select
          value={filter.migration || ''}
          onChange={(e) =>
            onChange({
              ...filter,
              migration: e.target.value as UsersFilter['migration'],
            })
          }
          className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white"
        >
          <option value="">All Accounts</option>
          <option value="retrieved">Legacy: retrieved</option>
          <option value="unretrieved">Legacy: not retrieved</option>
        </select>

        {/* Sort */}
        <select
          value={`${filter.sortBy || 'createdAt'}-${filter.sortOrder || 'desc'}`}
          onChange={(e) => {
            const [sortBy, sortOrder] = e.target.value.split('-') as [
              UsersFilter['sortBy'],
              UsersFilter['sortOrder']
            ];
            onChange({ ...filter, sortBy, sortOrder });
          }}
          className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white"
        >
          <option value="createdAt-desc">Newest First</option>
          <option value="createdAt-asc">Oldest First</option>
          <option value="displayName-asc">Name A-Z</option>
          <option value="displayName-desc">Name Z-A</option>
          <option value="lastLoginAt-desc">Last Login</option>
        </select>
      </div>

      {/* Results count */}
      <div className="mt-3 text-sm text-gray-500">
        {totalCount} user{totalCount !== 1 ? 's' : ''} found
      </div>
    </div>
  );
};

const SearchIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
    />
  </svg>
);

export default UserFilters;
